import { useEffect, useMemo, useState } from 'react'
import { useVerifiedCallerIdsQuery } from 'v2/pages/settings/forwarding-contacts/queries'
import { useAccountNumbersQuery } from 'v2/pages/settings/phone-numbers/queries'
import { useAgentSettingsQuery, useChannelConfigurationQuery } from 'v2/pages/settings/queries'
import { ConvChannels } from 'v2/types/api/conversations'

// Supported channels for configuration API
const SUPPORTED_CONFIG_CHANNELS = ['call', 'whatsapp', 'message']

export interface FromNumber {
  name: string
  phoneNumber: string
}

interface UseFromNumbersResult {
  accountNumbers:
    | Array<{
        id: string
        number: string
        name_tag: string
      }>
    | undefined
  defaultSender: FromNumber | undefined
  isLoading: boolean
}

export const pickDefaultNumber = <T extends { id: string }>(
  numbers: T[],
  agentDefault?: string | null,
  orgDefault?: string | null,
): T | undefined =>
  numbers.find((n) => n.id === agentDefault) ||
  numbers.find((n) => n.id === orgDefault) ||
  numbers[0]

export function useFromNumbers(channel: ConvChannels): UseFromNumbersResult {
  const [defaultSender, setDefaultSender] = useState<FromNumber>()

  const { data: channelConfiguration, isFetched: isChannelConfigFetched } =
    useChannelConfigurationQuery(channel)
  const {
    data: agentSettings,
    isFetched: isAgentSettingsFetched,
    isFetching: isAgentSettingsFetching,
  } = useAgentSettingsQuery()
  const { data: accountNumbers, isLoading: isLoadingAccountNumbers } = useAccountNumbersQuery(
    'simple',
    channel,
  )
  const { data: verifiedCallerIds, isLoading: isLoadingVerifiedCallerIds } =
    useVerifiedCallerIdsQuery()

  const isChannelSupported = SUPPORTED_CONFIG_CHANNELS.includes(channel)
  // Callers keep the first default they get, so don't pick one until every input that decides it
  // is in, and never from a cached agent default that a refetch is about to replace.
  const isDefaultPending =
    isLoadingAccountNumbers ||
    (isChannelSupported && !isChannelConfigFetched) ||
    (channel === ConvChannels.CALL &&
      (isLoadingVerifiedCallerIds || !isAgentSettingsFetched || isAgentSettingsFetching))

  const allNumbers = useMemo(() => {
    if (channel !== ConvChannels.CALL && !accountNumbers) return []

    const merged = [
      ...(accountNumbers ?? []),
      ...(channel === ConvChannels.CALL && verifiedCallerIds?.length
        ? verifiedCallerIds.map(({ phone_number, alias }) => ({
            id: phone_number,
            number: phone_number,
            name_tag: alias || '',
          }))
        : []),
    ]
    return merged
  }, [accountNumbers, verifiedCallerIds, channel])

  useEffect(() => {
    if (!allNumbers?.length || isDefaultPending) return

    const defaultNumber = pickDefaultNumber(
      allNumbers,
      channel === ConvChannels.CALL ? agentSettings?.default_call_number : undefined,
      isChannelSupported ? channelConfiguration?.default_caller_id : undefined,
    )
    if (!defaultNumber) return

    setDefaultSender({
      name: defaultNumber.name_tag || '',
      phoneNumber: defaultNumber.number,
    })
  }, [
    channelConfiguration,
    agentSettings,
    isDefaultPending,
    allNumbers,
    isChannelSupported,
    channel,
  ])

  return {
    accountNumbers: allNumbers,
    defaultSender,
    isLoading: isLoadingVerifiedCallerIds || isDefaultPending,
  }
}
